// src/pages/AppHomePage.js
import React from 'react';
import { Link } from 'react-router-dom';
import {
  Search,
  ArrowRight,
  LayoutDashboard,
  Compass,
  Sparkles,
} from 'lucide-react';
import useAuth from '../hooks/useAuth';
import { getAccessibleModules } from '../config/modules';
import { openOnboardingGuide } from '../components/onboarding/OnboardingGuide';
import styles from './AppHomePage.module.css';

// ─────────────────────────────────────────────────────────────
// DONNÉES DES MODULES
// ─────────────────────────────────────────────────────────────
// La liste des modules vit désormais dans src/config/modules.js,
// partagée avec la visite guidée (OnboardingGuide) pour rester cohérente.

// ─────────────────────────────────────────────────────────────
// COMPOSANT PRINCIPAL
// ─────────────────────────────────────────────────────────────
const AppHomePage = () => {
  const { user } = useAuth();

  const visibleModules = getAccessibleModules(user);

  // Prénom ou fallback
  const firstName = user?.prenom || user?.email?.split('@')[0] || 'vous';

  return (
    <div className={styles.page}>

      {/* ── En-tête de bienvenue ── */}
      <header className={styles.hero}>
        <div className={styles.heroIcon}>
          <Sparkles size={28} strokeWidth={1.75} />
        </div>

        <div className={styles.heroText}>
          <h1 className={styles.heroTitle}>
            Bonjour,&nbsp;
            <span className={styles.heroName}>{firstName}</span>&nbsp;👋
          </h1>
          <p className={styles.heroSubtitle}>
            {visibleModules.length > 0
              ? 'Que souhaitez-vous accomplir aujourd\'hui ?'
              : 'Aucun module n\'est accessible pour votre compte. Contactez votre administrateur.'}
          </p>
        </div>

        {/* Actions rapides */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <button type="button" onClick={openOnboardingGuide} className={styles.heroAction}>
            <Compass size={15} strokeWidth={2} />
            Visite guidée
          </button>

          <Link to="/management" className={styles.heroAction}>
            <LayoutDashboard size={15} strokeWidth={2} />
            Pilotage
            <ArrowRight size={14} strokeWidth={2} />
          </Link>
        </div>
      </header>

      {/* ── Grille des modules ── */}
      {visibleModules.length > 0 && (
        <>
          <div className={styles.sectionLabel}>
            <span>Vos modules</span>
            <span className={styles.sectionCount}>{visibleModules.length}</span>
          </div>

          <div className={styles.grid}>
            {visibleModules.map((module) => (
              <ModuleCard key={module.key} {...module} />
            ))}
          </div>
        </>
      )}

      {/* ── État vide ── */}
      {visibleModules.length === 0 && (
        <EmptyState />
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// CARTE MODULE
// ─────────────────────────────────────────────────────────────
const ModuleCard = ({ title, desc, path, Icon, color }) => (
  <Link to={path} className={`${styles.card} ${styles[`card--${color}`]}`}>

    {/* Fond décoratif */}
    <div className={styles.cardGlow} aria-hidden="true" />

    {/* Icône */}
    <div className={styles.cardIcon}>
      <Icon size={22} strokeWidth={1.75} />
    </div>

    {/* Texte */}
    <div className={styles.cardBody}>
      <h3 className={styles.cardTitle}>{title}</h3>
      <p  className={styles.cardDesc}>{desc}</p>
    </div>

    {/* Flèche */}
    <div className={styles.cardArrow}>
      <ArrowRight size={18} strokeWidth={2} />
    </div>
  </Link>
);

// ─────────────────────────────────────────────────────────────
// ÉTAT VIDE
// ─────────────────────────────────────────────────────────────
const EmptyState = () => (
  <div className={styles.empty}>
    <div className={styles.emptyIcon}>
      <Search size={32} strokeWidth={1.5} />
    </div>
    <h3 className={styles.emptyTitle}>Aucun module disponible</h3>
    <p  className={styles.emptyDesc}>
      Votre compte n'a accès à aucun module pour le moment.
      <br />
      Rapprochez-vous de votre administrateur.
    </p>
  </div>
);

export default AppHomePage;